<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';

import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';

import type { SidebarMenuChildLeaf, SidebarMenuItem } from '@/components/layout/i-layout-sidebar.vue';

import { useLoading } from '@/composables/loading';

import { client } from '@/services/client';

import { seatAllocationStore } from '@/store/seat-allocation';
import { userStore } from '@/store/user';

import { isDev, isDevOrDemo } from '@/utility/env';

import { type Confirm, confirmKey } from '@/injection-keys';

const { loading } = useLoading();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const confirmWithDialog = useConfirm();
const { t } = useI18n();

const confirm: Confirm = ((confirmationOptions, severity = 'danger') => new Promise(
  resolve => {
    confirmWithDialog.require({
      rejectClass: 'p-button-secondary p-button-text',
      rejectLabel: t('common.cancel'),
      acceptClass: severity === 'danger' ? 'p-button-danger' : undefined,
      accept: () => resolve(true),
      reject: () => resolve(false),
      ...confirmationOptions,
    });
  },
));

provide(confirmKey, confirm);

const menuItems = computed<SidebarMenuItem[]>(() => {
  if (!userStore.loggedIn) {
    return [];
  }

  const showDevTools = isDev || ('dev' in route.query);

  const accessControl = <T extends SidebarMenuItem | SidebarMenuChildLeaf>(
    allow: boolean,
    item: T,
  ): T | undefined => (allow ? item : undefined);

  const items: Array<SidebarMenuItem | undefined> = [
    accessControl(userStore.user.role.canSeeDashboard, {
      label: t('views.dashboard.title'),
      icon: fasChartSimple,
      route: { name: 'Dashboard' },
    }),
    accessControl(userStore.user.role.canSeeTargets, {
      label: t('views.targets.title'),
      icon: fasBullseyeArrow,
      route: { name: 'TargetManagement' },
    }),
    accessControl(userStore.user.role.canSeeRules, {
      label: t('views.rules.title'),
      icon: fasUsers,
      route: { name: 'RuleManagement' },
    }),
    accessControl(userStore.user.role.canSeeChannels, {
      label: t('views.channels.title'),
      icon: fasCompass,
      route: { name: 'ChannelManagement' },
    }),
    accessControl(userStore.user.role.canSeeUsers, {
      label: t('views.userManagement.title'),
      icon: fasUser,
      children: [
        accessControl(userStore.user.role.canSeeUsers, {
          label: t('views.userManagement.title'),
          route: { name: 'UserManagement' },
        })!,
        accessControl(userStore.user.role.canSeeUsers, {
          label: t('common.skills'),
          route: { name: 'SkillManagement' },
        })!,
        accessControl(userStore.user.role.canSeeUsers, {
          label: t('views.teamManagement.title'),
          route: { name: 'TeamManagement' },
        })!,
      ],
    }),
    accessControl(userStore.user.role.canSeeCatalog, {
      label: t('views.catalog.title'),
      icon: fasRectangleVerticalHistory,
      route: { name: 'CatalogManagement' },
    }),
    accessControl(
      userStore.user.role.canSeeUserRoles
      || userStore.user.role.canSeeBilling
      || userStore.user.role.canSeeAnswers
      || userStore.user.role.canEditSettings
      || userStore.user.role.canSeeAuditLogs
      || userStore.user.role.canSeeSsoConfig,
      {
        label: t('views.administration.title'),
        icon: fasGear,
        children: [
          accessControl(userStore.user.role.canSeeUserRoles, {
            label: t('views.userRoleManagement.title'),
            route: { name: 'UserRoleManagement' },
          })!,
          accessControl(userStore.user.role.canSeeAnswers, {
            label: t('views.answerManagement.title'),
            route: { name: 'AnswerManagement' },
          })!,
          accessControl(userStore.user.role.canSeeTariffs, {
            label: t('views.tariffs.title'),
            route: { name: 'TariffManagement' },
          })!,
          accessControl(userStore.user.role.canSeeBilling, {
            label: t('views.billing.title'),
            route: { name: 'Billing' },
          })!,
          accessControl(userStore.user.role.canEditSettings, {
            label: t('views.settings.title'),
            route: { name: 'Settings' },
          })!,
          accessControl(userStore.user.role.canSeeAuditLogs, {
            label: t('views.auditLogs.title'),
            route: { name: 'AuditLogs' },
          })!,
          accessControl(userStore.user.role.canSeeSsoConfig, {
            label: t('views.ssoConfig.title'),
            route: { name: 'SSOConfig' },
          })!,
        ],
      },
    ),
    // Every entry here is additionally denied by the router guard unless the role carries
    // canEditSettings - accessControl only hides the nav item, so it is presentation, not
    // access control. See isRoleRestrictedRoute in router/guards/auth.ts.
    accessControl(showDevTools && userStore.user.role.canEditSettings, {
      label: 'Entwicklertools',
      icon: fasSheep,
      children: [
        {
          label: 'IOs',
          route: { name: 'DevInteractionOpportunities' },
        },
        {
          label: 'Recalc Trigger',
          route: { name: 'DevRecalcTrigger' },
        },
        {
          label: 'Feature Flags',
          route: { name: 'DevFeatureFlags' },
        },
        {
          label: 'Valkey',
          route: { name: 'DevValkey' },
        },
        // isKmhStage, not isDevDemoOrTest: every endpoint this view calls is gated on the
        // backend's isKmhStage (local/develop/demo), which deliberately excludes the test
        // stage so it mirrors customer production. Offering the entry on test. advertised a
        // page whose four requests all 403.
        accessControl(isDevOrDemo, {
          label: 'Kundendaten',
          route: { name: 'DevCustomersOverview' },
        })!,
      ],
    }),
  ];

  const accessableMenuItem = <T extends SidebarMenuChildLeaf | SidebarMenuItem>(item: T | undefined): item is T => !!item;

  return items
    .filter(accessableMenuItem)
    .map(item => {
      if ('children' in item) {
        return {
          ...item,
          children: item.children.filter(accessableMenuItem),
        };
      }
      return item;
    });
});

const sidebarMini = ref(false);

function wakeUpCore() {
  return client.post('/wakeup', {});
}

const isLoggedIn = computed(() => userStore.loggedIn);

watchEffect(() => {
  if (isLoggedIn.value) {
    void wakeUpCore();
  }
});

axios.interceptors.response.use(
  response => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error?.response) {
      const { response } = error;

      if (response.status === 401) {
        toast.add({
          severity: 'warn',
          summary: t('errors.toast.authentication.expired.title'),
          detail: t('errors.toast.authentication.expired.message'),
          life: 5_000,
        });

        userStore.user = undefined;
        userStore.loggedIn = false;

        void router.push({
          name: 'Login',
          query: {
            redirect: route.path,
          },
        });

        return new Promise(() => {});
      }

      if (response.status === 409 && response.data?.title && response.data?.message) {
        const {
          title: summary,
          message: detail,
        } = response.data;

        toast.add({
          severity: 'warn',
          summary,
          detail,
          life: 5_000,
        });

        loading.value = false;
        return new Promise(() => {});
      }
    }

    return Promise.reject(error);
  },
);
</script>

<template>
  <i-layout
    data-test="app-layout"
    :loading
    :is-logged-in
    :class="{
      'collapsed': sidebarMini,
      'expanded': !sidebarMini,
      'login-screen': !isLoggedIn,
    }"
  >
    <template #header>
      <i-layout-header
        v-if="isLoggedIn"
        data-test="layout-header"
        @toggle-sidebar="sidebarMini = !sidebarMini"
      />
    </template>

    <template #sidebar>
      <i-layout-sidebar
        v-if="isLoggedIn"
        data-test="layout-sidebar"
        :mini="sidebarMini"
        :menu-items
      />
    </template>

    <template #content>
      <suspense>
        <router-view />

        <template #fallback>
          <div class="size-full flex justify-center items-center">
            <div class="w-120">
              <img src="/src/assets/intellivend_logo.svg" alt="Intellivend Logo">
              <i-progress-bar />
            </div>
          </div>
        </template>
      </suspense>
    </template>
  </i-layout>

  <i-seat-warning-dialog
    v-model:visible="seatAllocationStore.warningDialog.show"
    :can-edit-billing="seatAllocationStore.warningDialog.canEditBilling"
    :can-edit-users="seatAllocationStore.warningDialog.canEditUsers"
    :seat-allocated="seatAllocationStore.warningDialog.seatAllocated"
  />

  <p-confirm-dialog
    :draggable="false"
    class="w-md aspect-auto"
    :pt="{
      root: { 'data-test': 'confirm-dialog' },
      title: { 'data-test': 'dialog-title' },
      message: { 'data-test': 'confirm-dialog-message' },
    }"
  />
  <p-toast
    position="bottom-right"
    :pt="{
      message: { 'data-test': 'toast-message' },
      summary: { 'data-test': 'toast-summary' },
      detail: { 'data-test': 'toast-detail' },
    }"
  />
</template>

<style lang="scss">
.collapsed {
  --p-layout-sidebar-width: 5rem;
}
.expanded {
  --p-layout-sidebar-width: 17.5rem;
}
.login-screen {
  --p-layout-sidebar-width: 0;
  --p-layout-header-height: 0;
  --p-layout-page-padding: 0;

  .layout-page {
    height: 100vh;
    width: 100vw;
    overflow-y: auto;
  }
}
</style>
